import axios from 'axios';
import { authedHeaders, getAccessToken, getTenantId } from '../store/authStore';
import type { MyEnrollment } from './academy';

// Enrolling in PAI Academy with the identity you are already logged in with.
// Enroll goes to academy-service; the handoff between your tenant and your
// Kitchen is auth-service's (it holds the signing key and the link table).

const ACADEMY_BASE = import.meta.env.VITE_ACADEMY_SERVICE_URL || 'http://localhost:8034';
const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';

export const KITCHEN_TENANT_PREFIX = 'tenant-learn-';
export const isKitchenTenant = (tenantId?: string | null) =>
  !!tenantId && tenantId.startsWith(KITCHEN_TENANT_PREFIX);

const client = axios.create({
  baseURL: ACADEMY_BASE,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});
client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenant = getTenantId();
  if (tenant) config.headers['X-Tenant-ID'] = tenant;
  return config;
});

export interface LinkedEnrollResult {
  linked: true;
  kitchen_tenant_id: string;
  enrollment: MyEnrollment['enrollment'];
  note?: string;
}

/** Enroll the CURRENT session's user: same email and name, no new password. */
export async function enrollLinked(trackKey: string): Promise<LinkedEnrollResult> {
  const { data } = await client.post('/academy/enrollments/linked', { track_key: trackKey });
  return data;
}

export type LinkStatus =
  | { linked: false }
  | { linked: true; side: 'origin' | 'kitchen'; origin_tenant_id: string; kitchen_tenant_id: string };

export async function fetchLinkStatus(): Promise<LinkStatus> {
  const res = await fetch(`${AUTH_API}/auth/academy-link/status`, {
    headers: authedHeaders(),
    credentials: 'include',
  });
  if (!res.ok) return { linked: false };
  return res.json();
}

/** Ask auth-service for the session on the other side of the link. The server
 *  replaces the refresh cookie; the caller then reloads so every store restarts
 *  under the new tenant. Throws with the server's detail. */
export async function switchSession(): Promise<{ direction: 'kitchen' | 'origin'; tenant_id: string }> {
  const res = await fetch(`${AUTH_API}/auth/academy-link/switch`, {
    method: 'POST',
    headers: authedHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === 'string' ? err.detail : `HTTP ${res.status}`);
  }
  return res.json();
}
